import { api } from '../api/vykoClient';
import { userService } from '../services/userService';
import { handoverTrackingService } from '../services/handoverTrackingService';
import { ActiveInpatient, NurseUser, PatientHandoverItem } from '../types';
import { appLogger } from '../utils/logger';
import { getPatientGender, renderPatientGenderIcon, getPatientDisplayName, formatDoctorName } from '../utils/patientHelper';

export class GiveHandoverModal {
  private mountPoint: HTMLElement;
  private patient: PatientHandoverItem | ActiveInpatient;
  private onSuccess: (assignedNurse?: NurseUser | null) => void;
  private onClose: () => void;

  private selectedNurse: NurseUser | null = null;
  private nurseSearchResults: NurseUser[] = [];
  private isSearchingNurses = false;
  private isSubmitting = false;
  private nurseSearchQuery = '';
  private handoverNotes = '';
  private searchDebounceTimer: number | null = null;
  private errorMessage = '';
  private isbarDetail = '';
  private isLoadingIsbar = false;
  private showIsbarPreview = false;

  constructor(
    mountPoint: HTMLElement,
    patient: PatientHandoverItem | ActiveInpatient,
    onSuccess: (assignedNurse?: NurseUser | null) => void,
    onClose?: () => void,
    preselectedNurse?: NurseUser | null
  ) {
    this.mountPoint = mountPoint;
    this.patient = patient;
    this.onSuccess = onSuccess;
    this.onClose = onClose || (() => this.destroy());

    if (preselectedNurse) {
      this.selectedNurse = preselectedNurse;
    }

    this.initPreselectedNurseAndRender();
  }

  private async initPreselectedNurseAndRender() {
    // If nurse was not provided in constructor, check remembered handover nurse from Cloudflare D1 / localStorage
    if (!this.selectedNurse && api.loginId) {
      try {
        const prefs = await userService.getUserPreferences(api.loginId);
        if (prefs.preferredHandoverNurse && !this.selectedNurse) {
          this.selectedNurse = prefs.preferredHandoverNurse;
        }
      } catch {}
    }

    this.render();
    if (!this.selectedNurse) {
      this.initialLoadNurses();
    }

    // On-demand verification of patient handover status via doctor-desk-mobile/doctor-visit-details
    const hosp = String(this.patient.hospitalNumber || '');
    const ip = String(this.patient.ipNumber || '');
    if (hosp && ip) {
      this.loadIsbarDetail();
      handoverTrackingService
        .verifyAssignment(hosp, ip)
        .then((updated) => {
          if (updated && !this.isSubmitting) {
            this.render();
          }
        })
        .catch(() => {});
    }
  }

  private async loadIsbarDetail() {
    const hosp = String(this.patient.hospitalNumber || '');
    const ip = String(this.patient.ipNumber || '');
    if (!hosp || !ip) return;
    this.isLoadingIsbar = true;
    try {
      this.isbarDetail = await api.fetchIsbarDetail(hosp, ip);
    } catch (e) {
      console.warn('Failed loading ISBAR details:', e);
    } finally {
      this.isLoadingIsbar = false;
      const previewContainer = this.mountPoint.querySelector('#isbar-preview-content');
      if (previewContainer) {
        previewContainer.innerHTML = this.isbarDetail
          ? this.isbarDetail
          : '<p style="color:var(--text-tertiary); margin: 0.25rem 0;">No additional clinical details found for this patient.</p>';
      }
    }
  }

  private async initialLoadNurses() {
    this.isSearchingNurses = true;
    this.updateNurseResultsList();
    try {
      this.nurseSearchResults = await api.searchNurses('', 1, 30);
    } catch (e: any) {
      console.warn('Could not load initial nurse list', e);
    } finally {
      this.isSearchingNurses = false;
      this.updateNurseResultsList();
    }
  }

  private async performNurseSearch(query: string) {
    this.nurseSearchQuery = query;
    this.isSearchingNurses = true;
    this.updateNurseResultsList();

    try {
      this.nurseSearchResults = await api.searchNurses(query, 1, 40);
    } catch (e: any) {
      console.error('Failed searching nurses', e);
    } finally {
      this.isSearchingNurses = false;
      this.updateNurseResultsList();
    }
  }

  public render() {
    const pName = getPatientDisplayName(this.patient);
    const hosp = String(this.patient.hospitalNumber || 'N/A');
    const ip = String(this.patient.ipNumber || 'N/A');
    const dept = this.patient.departmentName || (this.patient as any).department || '';
    const ward =
      (this.patient as ActiveInpatient).wardType ||
      (this.patient as any).ward ||
      (this.patient as any).wardName ||
      '';
    const doctorRaw =
      (this.patient as ActiveInpatient).doctorName ||
      (this.patient as any).DoctorName ||
      (this.patient as any).doctor ||
      '';
    const doctor = formatDoctorName(doctorRaw);

    const gender = getPatientGender(this.patient);
    const genderTitle = gender === 'male' ? 'Male Patient' : gender === 'female' ? 'Female Patient' : 'Patient';

    const currentAssignment = handoverTrackingService.getAssignment(hosp, ip);
    const isCurrentAccepted = currentAssignment?.ackStatus === 'NTC0002';

    const rawAge =
      this.patient.age !== undefined && this.patient.age !== null
        ? this.patient.age
        : (this.patient as any).ageYy ?? (this.patient as any).patientAge;
    const cleanAge = rawAge !== undefined && rawAge !== null ? String(rawAge).trim() : '';
    const hasAge = cleanAge !== '' && cleanAge.toLowerCase() !== 'null' && cleanAge.toLowerCase() !== 'undefined';
    const ageText = hasAge ? (/^\d+$/.test(cleanAge) ? (cleanAge === '0' ? '<1 Y' : `${cleanAge} Y`) : cleanAge) : '';

    const rawUnit =
      (this.patient as ActiveInpatient).deparmentUnit !== undefined && (this.patient as ActiveInpatient).deparmentUnit !== null
        ? (this.patient as ActiveInpatient).deparmentUnit
        : (this.patient as any).departmentUnit ?? (this.patient as any).unitNo ?? (this.patient as any).unit;
    const cleanUnit = rawUnit !== undefined && rawUnit !== null ? String(rawUnit).trim() : '';
    const hasUnit = cleanUnit !== '' && cleanUnit.toLowerCase() !== 'null' && cleanUnit.toLowerCase() !== 'undefined';
    const unitText = hasUnit ? cleanUnit.replace(/^unit\s*[-:#]?\s*/i, '') || cleanUnit : '';

    this.mountPoint.innerHTML = `
      <div class="handover-clean-overlay vyko-modal-overlay animate-fade-in" id="give-handover-overlay">
        <div class="handover-clean-card vyko-modal-card animate-scale-up" role="dialog" aria-modal="true" aria-labelledby="handover-dialog-title">
          
          <!-- Mobile Drag Handle -->
          <div class="sheet-drag-handle"></div>

          <!-- Modal Header -->
          <div class="handover-clean-header vyko-modal-header">
            <div>
              <h2 id="handover-dialog-title" class="handover-clean-title vyko-modal-title">Confirm Handover</h2>
              <p class="handover-clean-sub vyko-modal-sub">Transfer patient clinical responsibility</p>
            </div>

            <button class="handover-clean-close vyko-modal-close" id="btn-close-give-handover" title="Close" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="handover-clean-body vyko-modal-body">
            ${this.errorMessage ? `
              <div class="handover-alert-error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span>${this.errorMessage}</span>
              </div>
            ` : ''}

            <!-- Minimal Patient Header Card -->
            <div class="handover-clean-patient-card">
              <div class="patient-card-top-row">
                <div class="patient-title-group">
                  <span class="patient-gender-avatar gender-${gender}" title="${genderTitle}" aria-hidden="true">
                    ${renderPatientGenderIcon(gender)}
                  </span>
                  <span class="patient-card-name-text" title="${pName}">${pName}</span>
                </div>
                ${doctor ? `<span class="patient-card-doctor-pill" title="Attending: ${doctor}">${doctor}</span>` : ''}
              </div>
              <div class="patient-card-meta-line">
                <span>H.No: <strong>${hosp}</strong></span>
                <span class="meta-separator">•</span>
                <span>IP: <strong>${ip}</strong></span>
                ${ageText ? `
                  <span class="meta-separator">•</span>
                  <span>Age: <strong>${ageText}</strong></span>
                ` : ''}
                ${unitText ? `
                  <span class="meta-separator">•</span>
                  <span>Unit: <strong>${unitText}</strong></span>
                ` : ''}
                ${dept ? `
                  <span class="meta-separator">•</span>
                  <span>Dept: <strong>${dept}</strong></span>
                ` : ''}
                ${ward ? `
                  <span class="meta-separator">•</span>
                  <span>Ward: <strong>${ward}</strong></span>
                ` : ''}
              </div>
            </div>

            <!-- Existing Assignment Banner (if already assigned) -->
            ${currentAssignment ? `
              <div class="handover-clean-status-banner ${isCurrentAccepted ? 'status-accepted' : 'status-pending'}">
                <span class="clean-status-dot"></span>
                <span>Currently assigned to <strong>${currentAssignment.targetNurseName}</strong> (${isCurrentAccepted ? 'Accepted' : 'Pending'}). Confirming will reassign care.</span>
              </div>
            ` : ''}

            <!-- Incoming Shift Nurse (Recipient) -->
            <div class="handover-clean-section">
              <div class="clean-section-header">
                <span class="clean-section-title">Assign Care To</span>
                <span class="clean-section-hint">${this.selectedNurse ? 'Recipient Ready' : 'Required'}</span>
              </div>

              <div id="selected-nurse-container">
                ${this.selectedNurse ? `
                  <div class="clean-nurse-selected-card">
                    <div class="clean-nurse-avatar">
                      ${this.selectedNurse.userName.charAt(0).toUpperCase()}
                    </div>
                    <div class="clean-nurse-details">
                      <div class="clean-nurse-name">${this.selectedNurse.userName}</div>
                      <div class="clean-nurse-sub">${this.selectedNurse.loginId ? `ID: ${this.selectedNurse.loginId}` : ''} ${this.selectedNurse.employeeId ? `• EMP: ${this.selectedNurse.employeeId}` : ''}</div>
                    </div>
                    <button class="btn-clean-nurse-change" id="btn-clear-selected-nurse" type="button">
                      Change
                    </button>
                  </div>
                ` : `
                  <div class="clean-nurse-search-box">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      type="text"
                      id="input-nurse-search"
                      placeholder="Search nurse by name or login ID..."
                      value="${this.nurseSearchQuery}"
                      autocomplete="off"
                    />
                    ${this.isSearchingNurses ? `
                      <div class="spinner" style="width: 14px; height: 14px; border-width: 2px;"></div>
                    ` : ''}
                  </div>
                `}
              </div>

              <!-- Nurse Search Results List -->
              <div class="clean-nurse-results ${this.selectedNurse ? 'hidden' : ''}" id="nurse-results-dropdown">
                <!-- Rendered dynamically -->
              </div>
            </div>

            <!-- Handover Remarks & Clean Presets -->
            <div class="handover-clean-section">
              <div class="clean-section-header">
                <span class="clean-section-title">Handover Note / Instructions</span>
                <span class="clean-section-hint">Optional</span>
              </div>

              <div class="clean-preset-row">
                <button type="button" class="btn-clean-preset" data-preset="Shift handover to colleague. Patient stable and monitored.">Routine Handover</button>
                <button type="button" class="btn-clean-preset" data-preset="Patient is stable, all scheduled medications administered.">Meds Given</button>
                <button type="button" class="btn-clean-preset" data-preset="Post-operative observation. Regular vitals and wound check required.">Post-Op Check</button>
                <button type="button" class="btn-clean-preset" data-preset="Patient is NPO / Fasting for scheduled procedure. IV fluids ongoing.">NPO / Fasting</button>
              </div>

              <textarea
                id="textarea-handover-notes"
                class="handover-clean-input-area"
                rows="2"
                placeholder="Enter clinical observations, pending tasks, or instructions for the incoming nurse..."
              >${this.handoverNotes}</textarea>
            </div>

            <!-- Clinical ISBAR Accordion -->
            <div class="clean-isbar-accordion ${this.showIsbarPreview ? 'is-open' : ''}" id="isbar-accordion">
              <button type="button" class="clean-isbar-toggle" id="btn-toggle-isbar">
                <div class="clean-isbar-toggle-left">
                  <span class="clean-isbar-badge">ISBAR</span>
                  <span>View Clinical Handover Details (Vitals & Labs)</span>
                </div>
                <svg class="clean-isbar-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              <div class="clean-isbar-drawer" id="isbar-preview-content">
                ${this.isLoadingIsbar ? `
                  <div style="display:flex; align-items:center; gap:0.5rem; padding:0.5rem 0;">
                    <div class="spinner" style="width:14px; height:14px; border-width:2px;"></div>
                    <span>Loading patient ISBAR details...</span>
                  </div>
                ` : (this.isbarDetail ? this.isbarDetail : '<p style="color:var(--text-tertiary); margin: 0.25rem 0;">Clinical handover details will be attached from ISBAR on submission.</p>')}
              </div>
            </div>

          </div>

          <!-- Sticky Responsive Footer Actions -->
          <div class="handover-clean-footer vyko-modal-footer">
            <button class="btn btn-secondary btn-clean-cancel" id="btn-cancel-give-handover" type="button" ${this.isSubmitting ? 'disabled' : ''}>
              Cancel
            </button>
            <button
              class="btn btn-primary btn-clean-submit"
              id="btn-submit-give-handover"
              type="button"
              ${!this.selectedNurse || this.isSubmitting ? 'disabled' : ''}
            >
              ${this.isSubmitting ? `
                <div class="spinner" style="width: 14px; height: 14px; border-width: 2px; border-color: #fff #fff transparent transparent;"></div>
                <span>Assigning...</span>
              ` : `
                <span>Confirm & Assign</span>
              `}
            </button>
          </div>

        </div>
      </div>
    `;

    this.attachEvents();
    this.updateNurseResultsList();
  }

  private updateNurseResultsList() {
    const dropdown = this.mountPoint.querySelector('#nurse-results-dropdown');
    if (!dropdown || this.selectedNurse) return;

    if (this.isSearchingNurses && this.nurseSearchResults.length === 0) {
      dropdown.innerHTML = Array.from({ length: 3 })
        .map(
          () => `
          <div class="clean-nurse-row skeleton-row">
            <div class="skeleton-circle" style="width: 28px; height: 28px; flex-shrink: 0;"></div>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 0.25rem;">
              <div class="skeleton-line" style="width: 50%; height: 12px; border-radius: 4px;"></div>
              <div class="skeleton-line" style="width: 30%; height: 10px; border-radius: 4px; opacity: 0.6;"></div>
            </div>
          </div>
        `
        )
        .join('');
      return;
    }

    if (this.nurseSearchResults.length === 0) {
      dropdown.innerHTML = `
        <div class="clean-nurse-empty">
          <div class="clean-nurse-empty-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <line x1="17" y1="11" x2="23" y2="11"/>
            </svg>
          </div>
          <span class="clean-nurse-empty-text">${this.nurseSearchQuery ? `No nurses found matching "${this.nurseSearchQuery}"` : 'Type a nurse name or employee ID to search'}</span>
        </div>
      `;
      return;
    }

    dropdown.innerHTML = this.nurseSearchResults
      .map((nurse) => {
        const name = nurse.userName || nurse.loginId;
        const empId = nurse.employeeId || nurse.empId || nurse.id;
        const initials = name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || 'N';

        return `
          <div class="clean-nurse-row" data-nurse-id="${nurse.id}" data-login-id="${nurse.loginId}" data-name="${name}" data-emp-id="${empId}">
            <div class="clean-nurse-avatar-small">${initials}</div>
            <div class="clean-nurse-meta">
              <span class="clean-nurse-name-text">${name}</span>
              <span class="clean-nurse-sub-text">${empId ? `<span class="nurse-pick-id-tag">EMP: ${empId}</span>` : (nurse.loginId ? `<span class="nurse-pick-id-tag">ID: ${nurse.loginId}</span>` : '')}</span>
            </div>
            <button type="button" class="clean-nurse-select-btn">Select</button>
          </div>
        `;
      })
      .join('');

    // Attach click events on nurse items
    const items = dropdown.querySelectorAll('.clean-nurse-row');
    items.forEach((item) => {
      item.addEventListener('click', () => {
        const loginId = item.getAttribute('data-login-id') || '';
        const name = item.getAttribute('data-name') || '';
        const id = item.getAttribute('data-nurse-id') || '';
        const empId = item.getAttribute('data-emp-id') || id;

        this.selectedNurse = {
          id,
          userName: name,
          loginId,
          empId,
          employeeId: empId,
        };

        // Persist to Cloudflare D1 so user only needs to select once
        if (api.loginId) {
          userService.getUserPreferences(api.loginId).then((prefs) => {
            userService.saveUserPreferences(api.loginId!, {
              ...prefs,
              preferredHandoverNurse: this.selectedNurse,
            });
          });
        }

        this.render();
      });
    });
  }

  private attachEvents() {
    // Overlay backdrop click to close
    this.mountPoint.querySelector('#give-handover-overlay')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'give-handover-overlay' && !this.isSubmitting) {
        this.destroy();
      }
    });

    // Close button
    this.mountPoint.querySelector('#btn-close-give-handover')?.addEventListener('click', () => {
      if (!this.isSubmitting) this.destroy();
    });

    // ISBAR accordion toggle
    const btnToggleIsbar = this.mountPoint.querySelector('#btn-toggle-isbar');
    const isbarAccordion = this.mountPoint.querySelector('#isbar-accordion');
    btnToggleIsbar?.addEventListener('click', () => {
      this.showIsbarPreview = !this.showIsbarPreview;
      if (isbarAccordion) {
        isbarAccordion.classList.toggle('is-open', this.showIsbarPreview);
      }
    });

    // Cancel button
    this.mountPoint.querySelector('#btn-cancel-give-handover')?.addEventListener('click', () => {
      if (!this.isSubmitting) this.destroy();
    });

    // Clear selected nurse button
    this.mountPoint.querySelector('#btn-clear-selected-nurse')?.addEventListener('click', () => {
      this.selectedNurse = null;
      this.render();
    });

    // Search input
    const searchInput = this.mountPoint.querySelector('#input-nurse-search') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      const val = (e.target as HTMLInputElement).value;
      if (this.searchDebounceTimer !== null) {
        window.clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = window.setTimeout(() => {
        this.performNurseSearch(val);
      }, 250);
    });

    // Notes textarea
    const notesArea = this.mountPoint.querySelector('#textarea-handover-notes') as HTMLTextAreaElement;
    notesArea?.addEventListener('input', (e) => {
      this.handoverNotes = (e.target as HTMLTextAreaElement).value;
    });

    // Quick presets
    const presetBtns = this.mountPoint.querySelectorAll('.btn-clean-preset');
    presetBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const text = btn.getAttribute('data-preset') || '';
        if (notesArea) {
          notesArea.value = text;
          this.handoverNotes = text;
          notesArea.focus();
        }
      });
    });

    // Submit Give Handover
    this.mountPoint.querySelector('#btn-submit-give-handover')?.addEventListener('click', () => {
      this.handleSubmit();
    });
  }

  private async handleSubmit() {
    if (!this.selectedNurse || this.isSubmitting) return;

    this.isSubmitting = true;
    this.errorMessage = '';
    this.render();

    const pName = (
      this.patient.patientName ||
      (this.patient as ActiveInpatient).displayName ||
      (this.patient as ActiveInpatient).firstName ||
      'Patient'
    ).trim();
    const hosp = String(this.patient.hospitalNumber);
    const ip = String(this.patient.ipNumber);

    appLogger.info(`Giving handover of ${pName} (H.No: ${hosp}) to ${this.selectedNurse.userName}...`);

    try {
      const result = await api.giveHandover({
        hospitalNumber: hosp,
        ipNumber: ip,
        targetNurseLoginId: this.selectedNurse.loginId,
        targetNurseName: this.selectedNurse.userName,
        targetNurse: this.selectedNurse,
        remarks: this.handoverNotes,
        patient: this.patient,
        isbarContent: this.isbarDetail,
      });

      if (result.success) {
        handoverTrackingService.recordAssignment({
          hospitalNumber: hosp,
          ipNumber: ip,
          targetNurseLoginId: this.selectedNurse.loginId,
          targetNurseName: this.selectedNurse.userName,
          targetNurseEmpId: this.selectedNurse.employeeId || this.selectedNurse.empId,
          assignedByLoginId: api.loginId || '',
          assignedByName: api.getUserDisplayName(),
          assignedAt: Date.now(),
          reportKey: result.reportKey,
          ackStatus: 'NTC0001',
          verified: true,
        });

        // Background verify via doctor-desk-mobile/doctor-visit-details
        setTimeout(() => {
          handoverTrackingService.verifyAssignment(hosp, ip).catch(() => {});
        }, 1500);

        appLogger.success(`Handover for ${pName} assigned to ${this.selectedNurse.userName} successfully.`);
        this.showSuccessFeedback(pName, this.selectedNurse.userName);
      } else {
        this.errorMessage = result.message || 'Failed to submit handover. Please try again.';
        this.isSubmitting = false;
        this.render();
      }
    } catch (err: any) {
      console.error('Handover submission error:', err);
      this.errorMessage = err?.message || 'Network error while submitting handover';
      this.isSubmitting = false;
      this.render();
    }
  }

  private showSuccessFeedback(patientName: string, nurseName: string) {
    this.mountPoint.innerHTML = `
      <div class="handover-clean-overlay vyko-modal-overlay" id="give-handover-overlay">
        <div class="handover-clean-card vyko-modal-card animate-scale-up" style="text-align: center; padding: 2.25rem 1.75rem;">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem;">
            <div style="width: 52px; height: 52px; border-radius: 50%; background: rgba(52, 199, 89, 0.12); color: var(--system-green); display: flex; align-items: center; justify-content: center;">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
          </div>
          <h2 style="font-size: 1.2rem; font-weight: 700; margin-bottom: 0.4rem; color: var(--text-primary);">Handover Assigned</h2>
          <p style="color: var(--text-secondary); font-size: 0.9rem; margin-bottom: 1.5rem; line-height: 1.45;">
            Care of <strong>${patientName}</strong> has been transferred to <strong>${nurseName}</strong>.
          </p>
          <button class="btn btn-primary" id="btn-success-done" style="min-width: 130px; margin: 0 auto; border-radius: var(--radius-pill);">
            Done
          </button>
        </div>
      </div>
    `;

    this.mountPoint.querySelector('#btn-success-done')?.addEventListener('click', () => {
      const nurse = this.selectedNurse;
      this.destroy();
      this.onSuccess(nurse);
    });
  }

  public destroy() {
    if (this.searchDebounceTimer !== null) {
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.mountPoint.innerHTML = '';
    this.onClose();
  }
}
